import React, { useMemo, useState, useCallback } from 'react';
import { Box, Typography, Paper, Chip, IconButton, Tooltip, Collapse, CircularProgress, Table, TableBody, TableCell, TableRow, Button, Link, Skeleton } from '@mui/material';
import { OpenInNew, ExpandMore, ExpandLess, FileDownload } from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import type { PharmacyMapTableProps, PharmacyProduct, UserContactInfo } from './types';
import { calculateDistance, formatPhoneForWhatsApp } from './utils';

/**
 * Компонент ячейки с телефоном и ссылкой на WhatsApp
 */
const PhoneCell: React.FC<{
  username: string;
  userContacts: Map<string, UserContactInfo>;
}> = ({ username, userContacts }) => {
  // Поиск без учета регистра
  const contact = userContacts.get(username.toLowerCase());

  if (!contact) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>
        -
      </Typography>
    );
  }

  const waPhone = formatPhoneForWhatsApp(contact.phone);

  return (
    <Tooltip title={contact.fullName} arrow placement="top">
      <Link
        href={`https://wa.me/${waPhone}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          textDecoration: 'none',

          fontSize: '0.8rem',
          fontWeight: 500,
          '&:hover': {
            textDecoration: 'underline',

          }
        }}
      >
        {contact.phone}
      </Link>
    </Tooltip>
  );
};

/**
 * Компонент таблицы с виртуализацией
 * Работает быстро даже с 1000+ строк
 */
export const PharmacyMapTable: React.FC<PharmacyMapTableProps> = ({
  locations,
  userLocation,
  onRowClick,
  searchType,
  searchQuery,
  productSearchResults,
  isSearching = false,
  onShowOnMap,
  userContacts,
  loadingContacts
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [pharmacyProducts, setPharmacyProducts] = useState<Map<string, PharmacyProduct[]>>(new Map());
  const [loadingProducts, setLoadingProducts] = useState<Map<string, boolean>>(new Map());
  const [lastSearchQuery, setLastSearchQuery] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  // Очистка состояния при изменении поискового запроса
  React.useEffect(() => {
    if (searchQuery !== lastSearchQuery) {
      setExpandedRows(new Set());
      setPharmacyProducts(new Map());
      setLoadingProducts(new Map());
      setLastSearchQuery(searchQuery);
    }
  }, [searchQuery, lastSearchQuery]);

  // Загрузка товаров конкретной аптеки
  const loadPharmacyProducts = useCallback(async (pharmacyGuid: string) => {
    if (!searchQuery || !pharmacyGuid) return;

    try {
      setLoadingProducts(prev => new Map(prev).set(pharmacyGuid, true));

      const response = await axios.get(
        `https://tbl.zerdegroup.kz/api/v1/geo/pharmacy-products/${pharmacyGuid}`,
        { params: { searchQuery } }
      );

      if (response.data.success) {
        setPharmacyProducts(prev => new Map(prev).set(pharmacyGuid, response.data.products || []));
      }
    } catch (error) {
      console.error('Ошибка при загрузке товаров аптеки:', error);
    } finally {
      setLoadingProducts(prev => {
        const newMap = new Map(prev);
        newMap.delete(pharmacyGuid);
        return newMap;
      });
    }
  }, [searchQuery]);

  // Переключение раскрытия строки товаров
  const toggleRow = useCallback((pharmacyGuid: string | undefined) => {
    if (!pharmacyGuid || searchType !== 'product' || !searchQuery) return;

    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pharmacyGuid)) {
        newSet.delete(pharmacyGuid);
      } else {
        newSet.add(pharmacyGuid);
        // Загрузка товаров если еще не загружены
        if (!pharmacyProducts.has(pharmacyGuid)) {
          loadPharmacyProducts(pharmacyGuid);
        }
      }
      return newSet;
    });
  }, [searchType, searchQuery, pharmacyProducts, loadPharmacyProducts]);

  // Получение остатка для аптеки из результатов поиска (матчим по phNumber = username)
  const getTotalStock = useCallback((username: string): number | null => {
    if (!username || searchType !== 'product') return null;
    const result = productSearchResults.find(r => r.phNumber?.toLowerCase() === username.toLowerCase());
    return result ? result.totalStock : null;
  }, [searchType, productSearchResults]);

  // Экспорт в Excel файл
  const handleExportToExcel = useCallback(async () => {
    if (!searchQuery || productSearchResults.length === 0) return;

    try {
      setIsExporting(true);

      // Загрузка товаров для всех аптек из результатов
      const allData: any[] = [];

      for (const result of productSearchResults) {
        try {
          const response = await axios.get(
            `https://tbl.zerdegroup.kz/api/v1/geo/pharmacy-products/${result.pharmacyGuid}`,
            { params: { searchQuery } }
          );

          if (response.data.success && response.data.products) {
            response.data.products.forEach((product: PharmacyProduct) => {
              allData.push({
                'Аптека': result.pharmacyName,
                'Адрес': result.pharmacyAddress,
                'Город': result.cityName,
                'Наименование': product.productName,
                'МНН': product.mnns || '-',
                'Категория': product.cat || '-',
                'Производитель': product.producer || '-',
                'Количество': product.stockCount,
                'Цена, ₸': product.stockPrice,
                'Штрихкод': product.barcode || '-'
              });
            });
          }
        } catch (error) {
          console.error(`Ошибка загрузки товаров для ${result.pharmacyName}:`, error);
        }
      }

      // Создание Excel файла
      const ws = XLSX.utils.json_to_sheet(allData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Товары');

      // ширина колонок
      ws['!cols'] = [
        { wch: 30 }, // Аптека
        { wch: 40 }, // Адрес
        { wch: 15 }, // Город
        { wch: 40 }, // Наименование
        { wch: 15 }, // МНН
        { wch: 20 }, // Категория
        { wch: 25 }, // Производитель
        { wch: 12 }, // Количество
        { wch: 12 }, // Цена
        { wch: 15 }  // Штрихкод
      ];

      // Генерируем имя файла
      const date = new Date().toLocaleDateString('ru-RU').replace(/\./g, '-');
      const fileName = `Поиск_${searchQuery}_${date}.xlsx`;

      // Скачиваем файл
      XLSX.writeFile(wb, fileName);

      console.log(` Экспортировано ${allData.length} товаров в Excel`);
    } catch (error) {
      console.error('Ошибка при экспорте в Excel:', error);
      alert('Ошибка при создании файла Excel');
    } finally {
      setIsExporting(false);
    }
  }, [searchQuery, productSearchResults]);

  // Подготовка данных с сортировкой по расстоянию
  const virtualListData = useMemo(() => {
    const username = localStorage.getItem('username');
    const data = locations.map(location => ({
      location,
      distance: userLocation
        ? calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          location.latitude,
          location.longitude
        )
        : null,
      isCurrentUser: !!(username && location.username?.toLowerCase() === username.toLowerCase()),
      onRowClick
    }));

    // Сортировка: сначала текущий пользователь, потом по расстоянию
    if (userLocation) {
      return data.sort((a, b) => {
        if (a.isCurrentUser) return -1;
        if (b.isCurrentUser) return 1;
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      });
    }

    return data;
  }, [locations, userLocation, onRowClick]);

  return (
    <Box sx={{ mt: 3, position: 'relative' }}>
      {/* Loading overlay */}
      {isSearching && searchType === 'product' && (
        <Box
          sx={{
            position: 'absolute',
            top: 80,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            backdropFilter: 'blur(2px)',
            borderRadius: 2
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Поиск товаров...
            </Typography>
          </Box>
        </Box>
      )}

      <Paper elevation={0} sx={{ maxHeight: '50vh', overflow: 'auto', borderRadius: '12px', border: '1px solid #e4e4e7', boxShadow: 'none' }}>
        {/* Заголовок с количеством */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            p: 2,
            borderBottom: '1px solid #e4e4e7',
            position: 'sticky',
            top: 0,
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem', color: '#09090b', letterSpacing: '-0.02em' }}>
            Список аптек
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`Найдено: ${locations.length}`}
              size="medium"
              sx={{ fontWeight: 600, borderRadius: '6px', bgcolor: '#f4f4f5', color: '#18181b' }}
            />

            {/* Кнопка экспорта - только для режима поиска товаров */}
            {searchType === 'product' && searchQuery && productSearchResults.length > 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <FileDownload />}
                onClick={handleExportToExcel}
                disabled={isExporting}
                disableElevation
                sx={{
                  textTransform: 'none',
                  bgcolor: '#18181b',
                  color: 'white',
                  borderRadius: '6px',
                  '&:hover': { bgcolor: '#27272a' }
                }}
              >
                {isExporting ? 'Экспорт...' : 'Скачать Excel'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Заголовок таблицы */}
        <Box
          sx={{
            display: 'flex',
            bgcolor: 'white',
            color: '#09090b',
            position: 'sticky',
            top: 64,
            zIndex: 2,
            borderBottom: '1px solid #e4e4e7',
            letterSpacing: '-0.01em'
          }}
        >
          {searchType === 'product' && searchQuery && (
            <Box sx={{ flex: '0 0 40px', px: 1, py: 1.5 }} />
          )}
          <Box sx={{ flex: '0 0 180px', px: 2, py: 1.5, fontSize: '0.875rem', fontWeight: 600 }}>
            Аптека
          </Box>
          <Box sx={{ flex: '1 1 auto', px: 2, py: 1.5, fontSize: '0.875rem', fontWeight: 600 }}>
            Адрес
          </Box>
          {/* <Box sx={{ flex: '0 0 100px', px: 2, py: 1.5, fontSize: '0.875rem', fontWeight: 600 }}>
            Номер
          </Box> */}
          <Box sx={{ flex: '0 0 100px', px: 2, py: 1.5, fontSize: '0.875rem', fontWeight: 600, textAlign: 'center' }}>
            Остаток
          </Box>
          <Box sx={{ flex: '0 0 120px', px: 2, py: 1.5, fontSize: '0.875rem', fontWeight: 600, textAlign: 'center' }}>
            Расстояние
          </Box>
          <Box sx={{ flex: '0 0 60px', px: 1, py: 1.5, fontSize: '0.875rem', fontWeight: 600, textAlign: 'center' }}>
            {/* Иконка */}
          </Box>
        </Box>

        {/* Строки таблицы */}
        {virtualListData.map((row, index) => {
          const { location, distance, isCurrentUser } = row;
          const pharmacyGuid = location.id;
          const isExpanded = !!(pharmacyGuid && expandedRows.has(pharmacyGuid));
          const products = pharmacyGuid ? pharmacyProducts.get(pharmacyGuid) : undefined;
          const isLoading = pharmacyGuid ? loadingProducts.get(pharmacyGuid) : false;
          const totalStock = getTotalStock(location.username);

          return (
            <Box key={index}>
              {/* Основная строка */}
              <Box
                onClick={() => {
                  if (searchType === 'product' && searchQuery && pharmacyGuid) {
                    toggleRow(pharmacyGuid);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid #f4f4f5',
                  backgroundColor: isCurrentUser
                    ? '#f4f4f5'
                    : index % 2 === 0
                      ? '#ffffff'
                      : '#fafafa',
                  cursor: (searchType === 'product' && searchQuery && pharmacyGuid) ? 'pointer' : 'default',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    backgroundColor: isCurrentUser ? '#e4e4e7' : '#f4f4f5',
                  }
                }}
              >
                {/* Индикатор раскрытия (только для режима товаров) */}
                {searchType === 'product' && searchQuery && (
                  <Box sx={{ flex: '0 0 40px', display: 'flex', justifyContent: 'center', color: '#71717a' }}>
                    {pharmacyGuid ? (
                      isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />
                    ) : (
                      <Box sx={{ width: 24 }} />
                    )}
                  </Box>
                )}

                <Box sx={{ flex: '0 0 180px', px: 2, py: 1.5, fontSize: '0.875rem', fontWeight: isCurrentUser ? 600 : 500, color: '#09090b' }}>
                  {location.username}
                  {isCurrentUser && (
                    <Chip
                      label="ВЫ"
                      size="small"
                      sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 'bold', bgcolor: '#18181b', color: 'white', borderRadius: '4px' }}
                    />
                  )}
                </Box>
                <Box sx={{ flex: '1 1 auto', px: 2, py: 1.5, fontSize: '0.8rem', color: '#52525b' }}>
                  {location.address || '-'}
                </Box>
                {/* <Box sx={{ flex: '0 0 100px', px: 2, py: 1.5 }}>
                  {loadingContacts ? (
                    <Skeleton variant="text" width={95} height={20} />
                  ) : (
                    <PhoneCell username={location.username} userContacts={userContacts} />
                  )}
                </Box> */}
                <Box sx={{
                  flex: '0 0 100px',
                  px: 2,
                  py: 1.5,
                  fontSize: '0.8rem',
                  textAlign: 'center',
                  color: totalStock && totalStock > 0 ? '#16a34a' : '#a1a1aa', // green-600 or zinc-400
                  fontWeight: totalStock && totalStock > 0 ? 600 : 400
                }}>
                  {totalStock !== null ? totalStock : '-'}
                </Box>
                <Box sx={{
                  flex: '0 0 120px',
                  px: 2,
                  py: 1.5,
                  fontSize: '0.875rem',
                  textAlign: 'center',
                  fontWeight: 500,
                  color: distance && distance < 5 ? '#16a34a' : '#71717a'
                }}>
                  {distance !== null && !isCurrentUser ? `${distance.toFixed(2)} км` : '-'}
                </Box>
                <Box sx={{ flex: '0 0 60px', px: 1, py: 1.5, textAlign: 'center' }}>
                  <Tooltip title="Показать на карте" placement="left">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowClick(location.username, location.latitude, location.longitude);
                        if (onShowOnMap) {
                          onShowOnMap();
                        }
                      }}
                      sx={{ color: '#71717a', '&:hover': { color: '#09090b', bgcolor: '#e4e4e7' } }}
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Раскрываемая секция с товарами */}
              {searchType === 'product' && searchQuery && pharmacyGuid && (
                <Collapse in={isExpanded} timeout={300}>
                  <Box sx={{ bgcolor: '#f9fafb', borderBottom: '1px solid #e0e0e0' }}>
                    {isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : products && products.length > 0 ? (
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f4f4f5', color: '#09090b', borderBottom: '1px solid #e4e4e7' }}>Наименование</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f4f4f5', color: '#09090b', borderBottom: '1px solid #e4e4e7' }}>МНН</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f4f4f5', color: '#09090b', borderBottom: '1px solid #e4e4e7' }}>Категория</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f4f4f5', color: '#09090b', borderBottom: '1px solid #e4e4e7' }}>Производитель</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f4f4f5', color: '#09090b', textAlign: 'center', borderBottom: '1px solid #e4e4e7' }}>Количество</TableCell>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f4f4f5', color: '#09090b', textAlign: 'right', borderBottom: '1px solid #e4e4e7' }}>Цена, ₸</TableCell>
                          </TableRow>
                          {products.map((product, idx) => (
                            <TableRow key={idx} sx={{ '& td': { borderBottom: '1px solid #e4e4e7' } }}>
                              <TableCell sx={{ color: '#09090b' }}>{product.productName}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem', color: '#71717a' }}>{product.mnns || '-'}</TableCell>
                              <TableCell sx={{ fontSize: '0.75rem', color: '#71717a' }}>{product.cat || '-'}</TableCell>
                              <TableCell sx={{ color: '#52525b' }}>{product.producer || '-'}</TableCell>
                              <TableCell sx={{ textAlign: 'center', fontWeight: 600, color: '#09090b' }}>{product.stockCount}</TableCell>
                              <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: '#09090b' }}>{product.stockPrice.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary" align="center">
                        Товары не найдены
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

export default PharmacyMapTable;

